import React, { useCallback, useState, useEffect } from 'react';

import type { FiveSymbolLocale } from '#common/types/locale';
import type { CustomButtonStyles } from '../../../CallToAction';

import AddToCartButton from '../../../../../components/Blocks/Store/Buttons/AddToCartButton/AddToCartButton';
import GoToCheckoutButton from '../../../../../components/Blocks/Store/Buttons/GoToCheckoutButton';
import { GlobalUniversalCart } from '../../../../../scripts/blocks/store/cart/GlobalUniversalCart';
import { UniversalCart } from '../../../../../scripts/blocks/store/cart/UniversalCart';
import { StoreAPIDataService } from '../../../../../scripts/blocks/store/services/StoreAPIDataService';
import { getTokenFromCookie } from '../../../../../scripts/helpers/common';
import { canShowCart, PlaceTypes } from '../../../../../utils/cart-helper';
import { generateDrmList } from '../../../../../utils/store-helper';
import { StoreConvertedDRMType } from '../../../../../utils/types';

import { StoreItem } from '#common/types/store';
import {
  customTypesToStoreItem,
  GK_TYPE,
  BUNDLE_TYPE,
} from '#common/utils/store';

import './style.pcss';

export const WithCartButton = ({
  children,
  customStyles,
  isMultipage,
  projectId,
  cartId,
  sku,
  itemType,
  locale,
  isNewScheme,
  enableCart,
}: {
  children: React.ReactNode;
  customStyles: CustomButtonStyles;
  isMultipage: boolean;
  projectId: string;
  cartId: string;
  sku: string;
  itemType: typeof GK_TYPE | typeof BUNDLE_TYPE;
  locale: FiveSymbolLocale;
  isNewScheme: boolean;
  enableCart: boolean;
}) => {
  const isCanShowCart = canShowCart({
    enableCart,
    isMultipage,
    isNewScheme,
    placeType: PlaceTypes.CUSTOM_BUTTON,
  });

  const [isItemInCart, setItemInCartState] = useState(false);
  const [storeDependencies, setStoreDependencies] = useState<{
    cart: UniversalCart;
    storeAPIDataService: any;
    storeData: any;
  }>(Object.create({}));
  const [drmList, setDrmList] = useState<StoreConvertedDRMType[]>([]);

  const computeStoreDependencies = useCallback(async () => {
    const userToken = getTokenFromCookie();
    const storeAPIDataService = StoreAPIDataService.getInstance(
      cartId,
      userToken,
      projectId,
      locale
    );
    const cart = GlobalUniversalCart.getInstance(
      projectId,
      cartId,
      storeAPIDataService,
      userToken
    );

    let storeData = storeAPIDataService.findBySku(sku);
    if (!storeData) {
      await storeAPIDataService.loadLackItems([
        {
          ...customTypesToStoreItem(itemType),
          sku,
        },
      ]);
      storeData = storeAPIDataService.findBySku(sku);
    }
    if (!storeData) {
      throw new Error(
        `The store data was not found in the data storage when initializing cart buttons; SKU – ${sku}`
      );
    }

    if (storeData.unit_items && storeData.unit_items.length === 1) {
      [storeData] = storeData.unit_items;
    }

    return {
      storeData,
      storeAPIDataService,
      cart,
    };
  }, [cartId, itemType, locale, projectId, sku]);

  useEffect(() => {
    if (isCanShowCart) {
      computeStoreDependencies().then((dependencies) => {
        const {
          cart,
          storeData,
        }: { cart: UniversalCart; storeData: StoreItem } = dependencies;
        cart.addListener(() => setItemInCartState(cart.hasSKU(sku))); // Attention! Checking SKU without DRM
        setStoreDependencies(dependencies);
        setDrmList(generateDrmList(storeData.unit_items));
      });
    }
  }, [computeStoreDependencies, sku]);

  return isCanShowCart ? (
    <div className='inline-cart-button__container'>
      {!isItemInCart && children}
      <div
        className='inline-cart-button__wrapper'
        style={
          {
            '--cart-btn-color': customStyles.color,
            '--cart-btn-bg-color': customStyles['--bg-color'],
          } as any
        }
      >
        <div className='store__cart-wrapper' data-cart-button-parent>
          <AddToCartButton
            sku={sku}
            locale={locale}
            isTextNeeded={false}
            hidden={isItemInCart}
            selectorList={drmList}
            addToCart={() => {}}
          />
        </div>
        <GoToCheckoutButton
          sku={sku}
          cart={storeDependencies.cart}
          locale={locale}
          hidden={!isItemInCart}
        />
      </div>
    </div>
  ) : (
    children
  );
};
